import type { TFunction } from 'i18next';
import { localizedName } from '@/components/localized-name';
import { EVENT_TYPES } from '@/features/memberships/membership-enums';
import {
  HOLDER_STATUS,
  VALIDITY_UNIT,
  VOUCHER_STATUS,
  type AdminVoucher,
  type RestrictionsInput,
  type ValidityUnit,
  type VoucherRestrictions,
} from './types';

/**
 * Text shared by the catalogue, management and report screens. Its own module
 * rather than exports from a page, which would break fast refresh for that file.
 */

export function validityLabel(
  amount: number,
  unit: ValidityUnit,
  t: TFunction
) {
  return unit === VALIDITY_UNIT.months
    ? t('vouchers.validityMonths', { count: amount })
    : t('vouchers.validityDays', { count: amount });
}

/** One line per restriction kind that is set; "any event" when none are. */
export function restrictionLines(
  restrictions: VoucherRestrictions,
  language: string,
  t: TFunction
): string[] {
  const names = (items: { nameEn: string | null; nameEs: string | null }[]) =>
    items.map((item) => localizedName(item, language) || '—').join(', ');

  const lines: string[] = [];
  if (restrictions.locations.length) {
    lines.push(`${t('vouchers.locations')}: ${names(restrictions.locations)}`);
  }
  if (restrictions.serviceTypes.length) {
    lines.push(
      `${t('vouchers.serviceTypes')}: ${names(restrictions.serviceTypes)}`
    );
  }
  if (restrictions.eventTypes.length) {
    const labels = restrictions.eventTypes.map((value) => {
      const option = EVENT_TYPES.find((type) => type.value === value);
      return option ? t(option.labelKey) : String(value);
    });
    lines.push(`${t('vouchers.eventTypes')}: ${labels.join(', ')}`);
  }
  if (restrictions.events.length) {
    lines.push(`${t('vouchers.events')}: ${names(restrictions.events)}`);
  }
  return lines.length ? lines : [t('vouchers.anyEventAnywhere')];
}

export type BadgeTone = 'success' | 'secondary' | 'warning' | 'destructive' | 'outline';

/**
 * The one status an admin needs from a row, folding the stored lifecycle and
 * the derived claimed / used / expired into a single badge. Lifecycle wins: a
 * voided voucher is "Voided" however many uses it had left.
 */
export function voucherState(
  voucher: Pick<
    AdminVoucher,
    'status' | 'holderStatus' | 'isExpired' | 'holder'
  >
): { key: string; tone: BadgeTone } {
  switch (voucher.status) {
    case VOUCHER_STATUS.pendingPayment:
      return { key: 'vouchers.state.pendingPayment', tone: 'outline' };
    case VOUCHER_STATUS.paymentFailed:
      return { key: 'vouchers.state.paymentFailed', tone: 'destructive' };
    case VOUCHER_STATUS.cancelled:
      return { key: 'vouchers.state.cancelled', tone: 'secondary' };
    case VOUCHER_STATUS.voided:
      return { key: 'vouchers.state.voided', tone: 'destructive' };
    case VOUCHER_STATUS.refunded:
      return { key: 'vouchers.state.refunded', tone: 'secondary' };
  }
  if (voucher.holderStatus === HOLDER_STATUS.used) {
    return { key: 'vouchers.state.used', tone: 'secondary' };
  }
  if (voucher.isExpired) {
    return { key: 'vouchers.state.expired', tone: 'warning' };
  }
  return voucher.holder
    ? { key: 'vouchers.state.claimed', tone: 'success' }
    : { key: 'vouchers.state.unclaimed', tone: 'outline' };
}

/**
 * The restriction pickers' value. Event types are strings because that is what
 * the multi-select holds; `toRestrictionsInput` turns them back into numbers.
 */
export interface RestrictionDraft {
  locationIds: string[];
  serviceTypeIds: string[];
  eventTypes: string[];
  eventIds: string[];
}

export const NO_RESTRICTIONS: RestrictionDraft = {
  locationIds: [],
  serviceTypeIds: [],
  eventTypes: [],
  eventIds: [],
};

export function toRestrictionDraft(r: VoucherRestrictions): RestrictionDraft {
  return {
    locationIds: r.locations.map((x) => x.id),
    serviceTypeIds: r.serviceTypes.map((x) => x.id),
    eventTypes: r.eventTypes.map(String),
    eventIds: r.events.map((x) => x.id),
  };
}

export function toRestrictionsInput(draft: RestrictionDraft): RestrictionsInput {
  return { ...draft, eventTypes: draft.eventTypes.map(Number) };
}
